/* eslint-disable @typescript-eslint/no-explicit-any */
import { Helmet } from 'react-helmet';
import { FormattedMessage, useIntl } from 'react-intl';
import { Button, Divider, Space } from 'antd';
import { Link } from 'react-router-dom';

import moment from 'moment';

import { PageProps } from '@/types/PageProps';

import {
  InfiniteTable,
  ColumnsType,
  AppPageHeader,
  TabContentWrapper,
} from '@/components/_common';
import EditIcon from '@/components/_common/EditIcon';

import { flatInfiniteData } from '@/utils/request/flatInfiniteData';

import { getTableScrollX } from '@/utils/table';
import { useEffect, useState } from 'react';
import {
  BankingEntryListReturn,
  BankingEntryQuery,
  Destination,
} from '@/core/domain/financial/BankingEntry/BankingEntry.types';
import { useBankingEntries } from '@/core/domain/financial/BankingEntry/BankingEntry.hooks';
import BankingEntryExtraFilters from '@/components/Financial/Accounts/BankingEntry/BankingEntryExtraFilters';
import { quantityFormatter } from '@/utils/number';
import ReversalIcon from '@/components/_common/ReversalIcon';
import { useDebounce, useDisclosure } from '@/hooks';
import BankingEntryReversalModal from '@/components/Financial/BankingEntry/BankingEntryReversalModal';
import StatusTag from '@/components/_common/StatusTag';
import { hasPermissions } from '@/utils/functions/hasPermissions';
import Authorities from '@/types/Authority';
import { convertParams, usePersistSearchParams } from '@/utils/queryParams';
import usePageSearchHistory from '@/hooks/pageSearchHistory';
function BankingEntryPage({ route }: PageProps) {
  const intl = useIntl();

  const [queryParams, setQueryParams] = useState<BankingEntryQuery>({
    order: 'DESC',
    ...convertParams<BankingEntryQuery>({
      search: 'string',
      companyId: 'number',
      code: 'string',
      description: 'string',
      documentNumber: 'string',
      creationDate: 'string',
      value: 'number',
      bankAccountId: 'number',
      nature: 'string',
      order: 'string',
    }),
  });

  const [modalReversal, modalReversalAction] = useDisclosure();
  const [bankingEntrySelected, setBankingEntrySelected] =
    useState<BankingEntryListReturn>();

  const queryDebounce = useDebounce(queryParams, 500);
  usePersistSearchParams(queryDebounce);

  const { setLocation } = usePageSearchHistory();
  useEffect(setLocation, [queryDebounce]);

  const { data, isFetching, fetchNextPage, hasNextPage } =
    useBankingEntries(queryDebounce);

  const bankingEntries = flatInfiniteData(data);

  const handleOpenModalReversal = (bankingEntry: BankingEntryListReturn) => {
    setBankingEntrySelected(bankingEntry);
    modalReversalAction.open();
  };

  const columns: ColumnsType<BankingEntryListReturn> = [
    {
      dataIndex: ['company', 'name'],
      title: intl.formatMessage({
        id: 'releaseBankEntry.list.header.companies',
      }),
      width: 170,
    },
    {
      dataIndex: 'code',
      title: intl.formatMessage({ id: 'releaseBankEntry.list.header.code' }),
      width: 120,
      render: (value) => {
        if (!value) {
          return '';
        }

        return value;
      },
    },
    {
      dataIndex: 'description',
      title: intl.formatMessage({
        id: 'releaseBankEntry.list.header.description',
      }),
      ellipsis: true,
      width: 200,
    },
    {
      dataIndex: 'documentNumber',
      title: intl.formatMessage({
        id: 'releaseBankEntry.list.header.document',
      }),
      ellipsis: true,
      width: 110,
    },
    {
      dataIndex: 'creationDate',
      title: intl.formatMessage({
        id: 'releaseBankEntry.list.header.releaseDate',
      }),
      width: 110,
      render: (value) => (value ? moment(value).format('DD/MM/YYYY') : '-'),
    },
    {
      dataIndex: 'value',
      title: intl.formatMessage({
        id: 'releaseBankEntry.list.header.value',
      }),
      width: 140,
      render: (value) => `R$ ${quantityFormatter(value)}`,
    },
    {
      dataIndex: 'bankAccountName',
      title: intl.formatMessage({
        id: 'releaseBankEntry.list.header.account',
      }),
      ellipsis: true,
      width: 180,
    },
    {
      dataIndex: 'destination',
      title: intl.formatMessage({
        id: 'releaseBankEntry.list.header.destination',
      }),
      width: 80,
      render: (value) => (
        <StatusTag
          label={intl.formatMessage({ id: `releaseBankEntry.${value}` })}
          color={value === Destination.expense ? 'red' : 'green'}
        />
      ),
    },
    {
      key: 'refund',
      dataIndex: 'refund',
      title: '',
      render: (_, row) => {
        if (row.refund) {
          return <StatusTag label="Estornado" color="yellow" />;
        }
      },
      width: 80,
    },
    {
      title: intl.formatMessage({ id: 'releaseBankEntry.list.header.actions' }),
      render: (_, row) => (
        <Space>
          <EditIcon
            disabled={
              !hasPermissions([Authorities.FINANCIAL_BANKING_ENTRIES_EDIT])
            }
            redirect={`/app/financial/bankingEntry/${row.id}`}
          />

          {hasPermissions([Authorities.FINANCIAL_BANKING_ENTRIES_REVERSE]) && (
            <>
              <Divider type="vertical" />

              <ReversalIcon
                onClick={() => handleOpenModalReversal(row)}
                disabled={
                  row.refund ||
                  !hasPermissions([
                    Authorities.FINANCIAL_BANKING_ENTRIES_REVERSE,
                  ])
                }
                iconProps={{
                  style: row.refund
                    ? { color: 'gray', cursor: 'not-allowed' }
                    : { color: 'black' },
                }}
              />
            </>
          )}
        </Space>
      ),
      width: 90,
      align: 'right',
      fixed: 'right',
    },
  ];

  return (
    <TabContentWrapper
      title={route.name}
      permissions={route.permissions}
      extra={
        <Space>
          <Link to="/app/financial/bankingEntry/create">
            <Button
              type="primary"
              disabled={
                !hasPermissions([Authorities.FINANCIAL_BANKING_ENTRIES_ADD])
              }
            >
              <FormattedMessage id="releaseBankEntry.create" />
            </Button>
          </Link>

          <Link to="/app/financial/cashMovement">
            <Button
              disabled={
                !hasPermissions([
                  Authorities.BANKING_ENTRIES_CASH_MOVEMENT_ADD,
                  Authorities.BANKING_ENTRIES_CASH_MOVEMENT,
                ])
              }
              type="primary"
            >
              <FormattedMessage id="releaseBankEntry.searchBar.cashMovement" />
            </Button>
          </Link>
        </Space>
      }
    >
      <Helmet title={intl.formatMessage({ id: route.name })} />

      <AppPageHeader
        onSearch={(value) =>
          setQueryParams((prev) => ({ ...prev, search: value }))
        }
        searchValue={queryParams.search}
        headerActions={({ isShowingFilters }) =>
          isShowingFilters && (
            <Button
              type="primary"
              onClick={() => setQueryParams({ order: 'DESC' })}
            >
              {intl.formatMessage({ id: 'commons.clear.filters' })}
            </Button>
          )
        }
        extraFilters={
          <BankingEntryExtraFilters
            queryParams={queryParams}
            setQueryParams={setQueryParams}
          />
        }
      />

      <InfiniteTable<BankingEntryListReturn>
        columns={columns}
        dataSource={bankingEntries}
        loading={isFetching}
        rowKey={(row) => row.id}
        hasNextPage={hasNextPage}
        fetchNextPage={fetchNextPage}
        scroll={{ x: getTableScrollX(columns) }}
      />

      <BankingEntryReversalModal
        open={modalReversal}
        onCancel={modalReversalAction.close}
        bankingEntryList={bankingEntrySelected}
      />
    </TabContentWrapper>
  );
}

export default BankingEntryPage;
